/**
 * Set a member's dial-in PIN (docs/plans/oncall-line-plan.md 6.1, 7.5 PUT
 * /members/:id/pin; WP-11). Admin only.
 *
 * The PIN guards the callback menu a member reaches by calling the line from
 * their registered cell: caller ID can be spoofed, so the PIN is the only
 * secret (6.1). Exactly six digits, typed twice; the server stores only an
 * scrypt hash, refuses trivial PINs (six equal digits, a straight run such as
 * 123456; plan 16, WP-06) and clears the failure count and any lock. The PIN
 * is never logged, never kept after the dialog closes, and never shown again:
 * the list says only whether one is set.
 */
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import Modal from '../../../ui/Modal';
import { fieldCls, primaryBtnCls, secondaryBtnCls } from '../../../ui/styles';
import { apiErrorMessage, setMemberPin } from '../../api/oncallApi';
import type { OncallMember } from '../../api/oncallApi';
import { formatDateTime } from '../../lib/format';

/** Why a PIN cannot be used, or null (the server's rules: members.js isWellFormedPin, isTrivialPin). */
export function pinProblem(pin: string): string | null {
  if (!/^\d{6}$/.test(pin)) return 'The PIN must be exactly 6 digits.';
  const d = pin.split('').map(Number);
  const steps = d.slice(1).map((v, i) => v - d[i]);
  if (steps.every((s) => s === 0) || steps.every((s) => s === 1) || steps.every((s) => s === -1)) {
    return 'Choose a PIN that is not six equal digits or a straight run like 123456.';
  }
  return null;
}

export interface PinModalProps {
  isOpen: boolean;
  member: OncallMember | null;
  /** The site's timezone, for the lock time. */
  timeZone: string | null | undefined;
  onClose: () => void;
  onSaved: (member: OncallMember) => void;
}

export default function PinModal({ isOpen, member, timeZone, onClose, onSaved }: PinModalProps) {
  const [pin, setPin] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const pinRef = useRef<HTMLInputElement>(null);

  // Nothing typed survives a close.
  useEffect(() => {
    setPin('');
    setRepeat('');
    setError(null);
    setSaving(false);
  }, [isOpen, member?.id]);

  if (!member) return null;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (saving) return;
    const problem = pinProblem(pin);
    if (problem) {
      setError(problem);
      pinRef.current?.focus();
      return;
    }
    if (repeat !== pin) {
      setError('The two entries do not match.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await setMemberPin(member.id, pin);
      setPin('');
      setRepeat('');
      onSaved(saved);
    } catch (err) {
      setError(apiErrorMessage(err, 'The PIN could not be set.'));
      setSaving(false);
    }
  };

  const digitsOnly = (value: string) => value.replace(/\D/g, '').slice(0, 6);

  return (
    <Modal isOpen={isOpen} onClose={saving ? () => {} : onClose} title={`Set PIN: ${member.display_name}`} maxWidth="max-w-md">
      <form onSubmit={handleSubmit} noValidate data-testid="oncall-pin-form" className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {member.display_name} enters this PIN after calling the line from their cell, to reach the callback menu. Tell
          them the PIN in person; it is never shown again.
        </p>
        {member.pin_locked_until && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
            The PIN is locked after 5 wrong entries until {formatDateTime(member.pin_locked_until, timeZone, { withZone: true })}.
            A new PIN clears the lock.
          </p>
        )}
        {error && (
          <p role="alert" data-testid="oncall-pin-error" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
            {error}
          </p>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="oncall-pin" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
              New PIN (6 digits)
            </label>
            <input
              id="oncall-pin"
              ref={pinRef}
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              value={pin}
              onChange={(e) => setPin(digitsOnly(e.target.value))}
              className={`${fieldCls} w-full tracking-widest tabular-nums`}
            />
          </div>
          <div>
            <label htmlFor="oncall-pin-repeat" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
              Repeat the PIN
            </label>
            <input
              id="oncall-pin-repeat"
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              value={repeat}
              onChange={(e) => setRepeat(digitsOnly(e.target.value))}
              className={`${fieldCls} w-full tracking-widest tabular-nums`}
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button type="button" onClick={onClose} disabled={saving} className={`${secondaryBtnCls} disabled:cursor-not-allowed disabled:opacity-50`}>
            Cancel
          </button>
          <button type="submit" disabled={saving} data-testid="oncall-pin-save" className={primaryBtnCls}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Set PIN
          </button>
        </div>
      </form>
    </Modal>
  );
}
